import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowRight, FiPhone, FiCalendar, FiClock, FiPhoneCall, FiArrowUp, FiArrowDown } = FiIcons;

const slides = [
  {
    id: 1,
    badge: "MOVE WITHOUT PAIN",
    titlePart1: "Expert Care For",
    titlePart2: "Your Recovery.",
    description: "Personalized physiotherapy treatments designed to get you back to the activities you love.",
    // Reliable Unsplash ID: Doctor/Physio helping patient
    image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&q=80&w=2070",
    cta: "Start Treatment"
  },
  {
    id: 2,
    badge: "MODERN FACILITIES",
    titlePart1: "Advanced Therapy",
    titlePart2: "Solutions.",
    description: "State-of-the-art equipment and specialized techniques for faster rehabilitation.",
    // Reliable Unsplash ID: Modern Medical/Gym
    image: "https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&q=80&w=2070",
    cta: "Visit Clinic"
  },
  {
    id: 3,
    badge: "COMPASSIONATE CARE",
    titlePart1: "Healing Hands,",
    titlePart2: "Caring Hearts.",
    description: "We treat the person, not just the injury. Experience care that understands you.",
    // Reliable Unsplash ID: Massage/Manual Therapy
    image: "https://images.unsplash.com/photo-1519823551278-64ac92734fb1?auto=format&fit=crop&q=80&w=2070",
    cta: "Book Now"
  }
];

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  useEffect(() => {
    const timer = setInterval(nextSlide, 6000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section id="home" className="relative bg-slate-50 overflow-hidden">
      {/* Main Slider Area */}
      <div className="relative min-h-[100dvh] lg:h-[85vh] lg:min-h-[600px] flex items-center pt-16 lg:pt-0 pb-32 lg:pb-0">
        <AnimatePresence mode='wait'>
          <motion.div
            key={currentSlide}
            className="absolute inset-0 w-full h-full"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
          >
            {/* Background Image */}
            <div className="absolute right-0 top-0 w-full lg:w-[70%] h-full">
              <img 
                src={slides[currentSlide].image} 
                alt="Physiotherapy" 
                className="w-full h-full object-cover object-center" 
              />
              {/* Gradients */}
              <div className="hidden lg:block absolute inset-0 bg-gradient-to-r from-slate-50 via-slate-50/80 to-transparent lg:via-slate-50/30 lg:from-slate-50 lg:w-2/3"></div>
              <div className="lg:hidden absolute inset-0 bg-gradient-to-t from-slate-50 via-slate-50/90 to-transparent"></div>
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 h-full flex items-center">
          <div className="grid lg:grid-cols-2 gap-12 w-full">
            {/* Left Content */}
            <AnimatePresence mode='wait'>
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 30 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="max-w-2xl mt-auto lg:mt-0 relative z-20"
              >
                {/* Badge */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="flex items-center gap-3 mb-4 lg:mb-6 bg-white/80 backdrop-blur-sm w-fit p-1 rounded-md border border-slate-200 shadow-sm"
                >
                  <span className="bg-[#5c95ff] text-white text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider">
                    BEST IN SURAT
                  </span>
                  <span className="text-slate-600 text-[11px] font-bold uppercase tracking-widest pr-2">
                    {slides[currentSlide].badge}
                  </span>
                </motion.div>

                {/* Heading */}
                <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.1] mb-4 lg:mb-6 tracking-tight">
                  <motion.span 
                    className="block text-[#0b1b35]"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                  >
                    {slides[currentSlide].titlePart1}
                  </motion.span>
                  <motion.span 
                    className="block text-[#5c95ff]"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    {slides[currentSlide].titlePart2}
                  </motion.span>
                </h1>

                {/* Description */}
                <motion.p 
                  className="text-base sm:text-lg text-slate-700 lg:text-slate-600 mb-8 leading-relaxed max-w-lg font-medium"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                >
                  {slides[currentSlide].description}
                </motion.p>

                {/* CTA Button */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                >
                  <a 
                    href="#contact"
                    className="group inline-flex items-center gap-4 bg-[#5c95ff] text-white pl-6 pr-2 py-2 rounded-lg font-bold text-base lg:text-lg hover:bg-blue-600 transition-all duration-300 shadow-lg shadow-blue-500/20"
                  >
                    <span className="py-2">{slides[currentSlide].cta}</span>
                    <div className="bg-white text-[#5c95ff] w-10 h-10 rounded-md flex items-center justify-center group-hover:scale-105 transition-transform">
                      <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
                    </div>
                  </a>
                </motion.div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Vertical Navigation */}
        <div className="absolute right-0 top-1/2 -translate-y-1/2 z-20 hidden lg:flex flex-col gap-3">
          <button 
            onClick={prevSlide}
            className="bg-white w-12 h-14 rounded-l-xl shadow-lg flex items-center justify-center text-slate-400 hover:text-[#5c95ff] hover:w-14 transition-all duration-300 group"
          >
            <SafeIcon icon={FiArrowUp} className="w-5 h-5" />
          </button>
          <button 
            onClick={nextSlide}
            className="bg-white w-12 h-14 rounded-l-xl shadow-lg flex items-center justify-center text-slate-400 hover:text-[#5c95ff] hover:w-14 transition-all duration-300 group"
          >
            <SafeIcon icon={FiArrowDown} className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Info Cards */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-20 -mt-20 lg:-mt-24 mb-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-0 shadow-xl md:shadow-2xl rounded-xl overflow-hidden">
          {/* Emergency */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="bg-[#5c95ff] p-6 lg:p-8 text-white group hover:bg-blue-600 transition-colors duration-300 rounded-xl md:rounded-none"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg lg:text-xl font-bold">Emergency Cases</h3>
              <SafeIcon icon={FiPhoneCall} className="w-6 h-6 lg:w-8 lg:h-8 opacity-80" />
            </div>
            <p className="mb-4 lg:mb-6 text-blue-100 text-sm leading-relaxed font-medium">
              Immediate attention for acute injuries. Call us for priority scheduling.
            </p>
            <a href="tel:9510918372" className="flex items-center font-bold text-lg hover:translate-x-2 transition-transform duration-300">
              <SafeIcon icon={FiPhone} className="mr-2" /> 95109 18372
            </a>
          </motion.div>

          {/* Timetable */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="bg-[#0b1b35] p-6 lg:p-8 text-white group hover:bg-[#132c54] transition-colors duration-300 rounded-xl md:rounded-none"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg lg:text-xl font-bold">Doctors Timetable</h3>
              <SafeIcon icon={FiCalendar} className="w-6 h-6 lg:w-8 lg:h-8 opacity-80" />
            </div>
            <p className="mb-4 lg:mb-6 text-slate-300 text-sm leading-relaxed font-medium">
              Expert physiotherapists available throughout the week.
            </p>
            <a href="#contact" className="inline-block border border-white/20 px-6 py-2 rounded-lg font-bold text-sm hover:bg-white hover:text-[#0b1b35] transition-all duration-300 uppercase tracking-wider">
              Book Slot
            </a>
          </motion.div>

          {/* Opening Hours */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6 }}
            className="bg-[#5c95ff] p-6 lg:p-8 text-white group hover:bg-blue-600 transition-colors duration-300 rounded-xl md:rounded-none"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg lg:text-xl font-bold">Opening Hours</h3>
              <SafeIcon icon={FiClock} className="w-6 h-6 lg:w-8 lg:h-8 opacity-80" />
            </div>
            <ul className="space-y-3 text-sm text-blue-100 font-medium">
              <li className="flex justify-between border-b border-blue-400/30 pb-2">
                <span>Mon - Sat</span>
                <span>9:00 - 13:00</span>
              </li>
              <li className="flex justify-between border-b border-blue-400/30 pb-2">
                <span>Mon - Sat</span>
                <span>16:00 - 20:30</span>
              </li>
              <li className="flex justify-between pt-1">
                <span>Sunday</span>
                <span>Closed</span>
              </li>
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;